import React from "react";

interface TimelineItem {
  timestamp: string;
  title: string;
  body?: string;
}

function isTimelineItem(value: unknown): value is TimelineItem {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.timestamp === "string" && typeof obj.title === "string";
}

/**
 * Chronological feed. Payload shape mirrors the workspace render-spec
 * compiler:
 *   { items: Array<{ timestamp, title, body? }> }
 *
 * Visual spec: see .timeline-item / .timeline-dot / .timeline-line /
 * .timeline-time / .timeline-content in sandbox-styles.css. Mirrors the
 * polish bar of EventTimeline.tsx (the canvas-events timeline) but for
 * arbitrary agent-emitted timeline blocks.
 */
export default function TimelineBlock({ payload }: { payload: Record<string, unknown> }) {
  const rawItems = Array.isArray(payload.items) ? payload.items : [];
  const items = rawItems.filter(isTimelineItem);

  if (items.length === 0) {
    return (
      <div
        aria-label="timeline-empty"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        No timeline items.
      </div>
    );
  }

  return (
    <div aria-label="timeline" className="relative">
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <div
            key={`${item.timestamp}-${idx}`}
            className="flex relative"
            style={{ gap: 12, paddingBottom: isLast ? 0 : 16 }}
          >
            <div className="relative" style={{ flexShrink: 0, width: 8 }}>
              <div
                className="rounded-full"
                style={{
                  width: 8,
                  height: 8,
                  marginTop: 6,
                  backgroundColor: "var(--c-primary)",
                }}
              />
              {!isLast && (
                <div
                  style={{
                    position: "absolute",
                    left: 3,
                    top: 16,
                    bottom: -16,
                    width: 1,
                    backgroundColor: "var(--c-border)",
                  }}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs" style={{ color: "var(--c-text-faint)", marginBottom: 2 }}>
                {item.timestamp}
              </div>
              <div className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
                {item.title}
              </div>
              {item.body && (
                <div className="text-sm" style={{ color: "var(--c-text-secondary)", marginTop: 2 }}>
                  {item.body}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
